/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable complexity */
/* eslint-disable quotes, max-len */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import moment from 'moment';
import * as React from 'react';
import { connect } from 'react-redux';
import { OnChangeValue } from 'react-select';
import { Button } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import { FormInput } from 'src/components/molecules/FormInput/FormInput';
import { FormInputVariant } from 'src/types';
import { useTheme } from 'styled-components';
import { updateAuthenticationAdmin } from '../../../creators/Organisation.creators';
import '../../../css/admin/AuthenticationAdmin.css';
import * as Types from '../../../types';
import OldConfirmDialog from '../../OldConfirmDialog';
import OIDCInput from './OIDCInput';
import SAMLInput from './SAMLInput';
import Vault2faInput from './Vault2FAInput';

interface Props {
  authenticationType: Types.AuthenticationType;
  slug: string;
  updateAuthenticationAdmin: (
    authenticationAdmin: Types.AuthenticationAdmin,
  ) => Promise<any>;
  authenticationAdmin: Types.AuthenticationAdmin;
  features: Types.Feature[];
}

interface State {
  showPassword: boolean;
  showInfoElement: boolean;
  confirmSettingsDialogOpen: boolean;
  successDialogOpen: boolean;
  changedAuthenticationType: boolean;
  loading: boolean;
  validAuthSettings: boolean;
  authenticationAdmin: Types.AuthenticationAdmin;
}

const InfoIconButton = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const isVaultVariant = theme.isVaultVariant;
  return (
    <IconButton
      className="info-button"
      aria-label="info"
      aria-haspopup="true"
      onClick={onClick}
      style={{
        color: isVaultVariant
          ? '#35a191'
          : theme.atlas.semantic.color.action.primary.default,
      }}
    >
      {children}
    </IconButton>
  );
};

const AuthSuccessIcon = () => {
  const theme = useTheme();
  const isVaultVariant = theme.isVaultVariant;
  return (
    <CheckCircleIcon
      fontSize="inherit"
      style={{
        color: isVaultVariant
          ? theme.newPalette.green.G200
          : theme.atlas.semantic.color.status.success.default,
        fontSize: '80px !important',
      }}
    />
  );
};

export class AuthenticationAdmin extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = {
      showPassword: false,
      showInfoElement: false,
      confirmSettingsDialogOpen: false,
      successDialogOpen: false,
      loading: false,
      changedAuthenticationType: false,
      validAuthSettings: false,
      authenticationAdmin: props.authenticationAdmin || {
        organisation: { authenticationType: props.authenticationType },
      },
    };
  }

  private closeConfimSettingsDialog = () => {
    this.setState({ confirmSettingsDialogOpen: false });
  };

  private closeSuccessDialog = () => {
    this.setState({ successDialogOpen: false });
  };

  private saveAuthenticationAdmin = async () => {
    this.setState({
      loading: true,
      confirmSettingsDialogOpen: false,
      changedAuthenticationType:
        this.state.authenticationAdmin.organisation.authenticationType !==
        this.props.authenticationType,
    });

    try {
      // If Vault Login selected, do not send oauthProvider details (they are only set for SSO authentication)
      const authAdmin: Types.AuthenticationAdmin = {
        organisation: { ...this.state.authenticationAdmin.organisation },
        oauthProvider: Types.isOIDCAuth(
          this.state.authenticationAdmin.organisation.authenticationType,
        )
          ? this.state.authenticationAdmin.oauthProvider
          : undefined,
        samlProvider: Types.isSAMLAuth(
          this.state.authenticationAdmin.organisation.authenticationType,
        )
          ? this.state.authenticationAdmin.samlProvider
          : undefined,
      } as Types.AuthenticationAdmin;

      const updateAuthSettingsResult =
        await this.props.updateAuthenticationAdmin(authAdmin);

      this.setState({
        authenticationAdmin: { ...updateAuthSettingsResult },
        validAuthSettings: false,
        successDialogOpen: this.state.changedAuthenticationType,
      });

      if (!this.state.successDialogOpen) {
        handleSuccessToast({
          toastMessage: 'Authentication administration successfully saved.',
        });
      }
    } catch (error) {
      handleErrorToast({
        toastMessage: 'Authentication administration could not be saved.',
      });
    }
    this.setState({ loading: false, changedAuthenticationType: false });
  };

  private updateOauthProvider = (
    previousAuthenticationType: Types.AuthenticationType,
    newAuthenticationType: Types.AuthenticationType,
  ) => {
    // If the authentication type is updated from Vault Login/2FA to SSO, create empty Oauth Provider fields
    return Types.isVaultAuth(previousAuthenticationType) &&
      newAuthenticationType !== Types.AuthenticationType.VAULT_LOGIN
      ? undefined
      : this.state.authenticationAdmin.oauthProvider;
  };

  private onChangeAuthenticationTypeSelect = async (
    newAuthenticationType: Types.AuthenticationType,
  ) => {
    await this.setState({
      authenticationAdmin: {
        ...this.state.authenticationAdmin,
        organisation: {
          ...this.state.authenticationAdmin.organisation,
          authenticationType: newAuthenticationType,
        },
        oauthProvider: this.updateOauthProvider(
          this.props.authenticationType,
          newAuthenticationType,
        ),
      },
      changedAuthenticationType:
        this.props.authenticationType !== newAuthenticationType,
    });

    this.validateAuthenticationAdmin(this.state.authenticationAdmin);
  };

  private onChangeSSOTextfields = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const key: string = event.currentTarget.id;
    const { value } = event.currentTarget;

    await this.setState({
      authenticationAdmin: {
        ...this.state.authenticationAdmin,
        oauthProvider: {
          ...this.state.authenticationAdmin.oauthProvider,
          [key]: value,
        } as Types.OauthProviderType,
      },
    });

    this.validateAuthenticationAdmin(this.state.authenticationAdmin);
  };
  private onChangeSAMLTextfields = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const key: string = event.currentTarget.id;
    const { value } = event.currentTarget;

    this.setState({
      authenticationAdmin: {
        ...this.state.authenticationAdmin,
        samlProvider: {
          ...this.state.authenticationAdmin.samlProvider,
          [key]: value,
        } as Types.SamlProviderType,
      },
    });

    this.validateAuthenticationAdmin(this.state.authenticationAdmin);
  };

  private validateAuthenticationAdmin = (
    authSettings: Types.AuthenticationAdmin,
  ) => {
    let validAuthSettings = true;

    if (authSettings.organisation.authenticationType === null) {
      validAuthSettings = false;
    }

    if (Types.isOIDCAuth(authSettings.organisation.authenticationType)) {
      const isD1P =
        authSettings.organisation.authenticationType ===
        Types.AuthenticationType.D1P_OIDC;

      if (isD1P) {
        // D1P requires issuer and clientId (public client - no clientSecret)
        if (
          !authSettings.oauthProvider?.issuer ||
          !authSettings.oauthProvider?.client_id
        ) {
          validAuthSettings = false;
        }
      } else if (
        !authSettings.oauthProvider ||
        Object.values(authSettings.oauthProvider).some(
          (oauthSetting) => oauthSetting === null || oauthSetting === '',
        )
      ) {
        validAuthSettings = false;
      }
    }

    if (Types.isSAMLAuth(authSettings.organisation.authenticationType)) {
      if (
        !authSettings.samlProvider ||
        Object.values(authSettings.samlProvider).some(
          (oauthSetting) => oauthSetting === null || oauthSetting === '',
        )
      ) {
        validAuthSettings = false;
      }
    }

    this.setState({
      validAuthSettings,
    });
  };

  private toggleInfoElement = () => {
    this.setState({
      showInfoElement: !this.state.showInfoElement,
    });
  };

  private toggle2FA = (newToggleValue: boolean) => {
    const authType = newToggleValue
      ? Types.AuthenticationType.VAULT_2FA
      : Types.AuthenticationType.VAULT_LOGIN;
    this.onChangeAuthenticationTypeSelect(authType);
  };

  public render() {
    const readableAuthName = Types.readableAuthName(
      this.state.authenticationAdmin.organisation.authenticationType,
    );

    const nonVaultAuthTypes = Object.values(Types.AuthenticationType).filter(
      (authType) => !Types.isVaultAuth(authType),
    );

    const dataIntegrationEnabled = isRequiredFeatureEnabled(
      this.props.features,
      Types.FeatureFlag.DATA_INTEGRATION,
    );
    const vaultAuthType =
      this.props.authenticationType === Types.AuthenticationType.VAULT_LOGIN
        ? Types.AuthenticationType.VAULT_LOGIN
        : Types.AuthenticationType.VAULT_2FA;
    const authOptions: DefaultOption[] = [
      { label: 'Email and Password', value: vaultAuthType },
      ...(dataIntegrationEnabled
        ? nonVaultAuthTypes.map((authType) => ({
            label: `Identity provider - ${Types.readableAuthName(authType)}`,
            value: authType,
          }))
        : []),
    ];
    const selectedAuthOption =
      authOptions.find(
        (option) =>
          option.value ===
          this.state.authenticationAdmin.organisation.authenticationType,
      ) ?? null;
    const isVaultAuth = Types.isVaultAuth(
      this.state.authenticationAdmin.organisation.authenticationType,
    );
    const isSAMLAuth = Types.isSAMLAuth(
      this.state.authenticationAdmin.organisation.authenticationType,
    );
    const isOIDCAuth = Types.isOIDCAuth(
      this.state.authenticationAdmin.organisation.authenticationType,
    );

    return (
      <React.Fragment>
        <div className="routing-body-content" data-testid="authentication-page">
          <form>
            <div
              className="authentication-admin-container"
              data-testid={'authentication-admin-container'}
            >
              <h1 className="authentication-admin-header">
                Authentication for Resolution Hub
              </h1>
              <p className="authentication-admin-description">
                On this page you&apos;ll be able to configure how Resolution Hub
                users log in.
              </p>

              <div className="authentication-admin-select-container">
                <label
                  className="authentication-admin-select-label"
                  htmlFor="authentication-admin-select"
                >
                  Log-in method
                </label>
                <div className="authentication-admin-select-input">
                  <FormInput
                    data-testid={'authentication-admin-select'}
                    options={authOptions}
                    value={selectedAuthOption}
                    onChange={(
                      option: OnChangeValue<DefaultOption, boolean>,
                    ) => {
                      if (!option || Array.isArray(option)) {
                        return;
                      }
                      const selectedOption = option as DefaultOption;
                      this.onChangeAuthenticationTypeSelect(
                        selectedOption.value as Types.AuthenticationType,
                      );
                    }}
                    variant={FormInputVariant.DROPDOWN}
                  />
                </div>
              </div>
            </div>

            <div className="authentication-admin-sso-menu">
              <h1 className="authentication-admin-subheader">
                {(isSAMLAuth || isOIDCAuth) &&
                  `${readableAuthName} Configuration`}
                {isVaultAuth && (
                  <span>
                    Two Factor Authentication (2FA)
                    <InfoIconButton onClick={this.toggleInfoElement}>
                      <InfoIcon id="2fa-info" />
                    </InfoIconButton>
                  </span>
                )}
              </h1>

              {this.state.authenticationAdmin.auditEntry && (
                <p className="authentication-admin-last-updated">
                  {`Configuration last updated: ${moment(
                    this.state.authenticationAdmin.auditEntry.createdAt,
                  )
                    .local()
                    .format('DD-MMM-YYYY')
                    .toUpperCase()}
                    by ${
                      this.state.authenticationAdmin.auditEntry.user.name
                    }, ${this.state.authenticationAdmin.auditEntry.user.role}`}
                </p>
              )}
              {isOIDCAuth && (
                <OIDCInput
                  authenticationType={
                    this.state.authenticationAdmin.organisation
                      .authenticationType
                  }
                  issuer={
                    this.state.authenticationAdmin.oauthProvider?.issuer || ''
                  }
                  clientId={
                    this.state.authenticationAdmin.oauthProvider?.client_id ||
                    ''
                  }
                  clientSecret={
                    this.state.authenticationAdmin.oauthProvider
                      ?.client_secret || ''
                  }
                  onChangeSSOTextfields={this.onChangeSSOTextfields}
                />
              )}
              {isSAMLAuth && (
                <>
                  <div className="consumer-box" data-testid={'consumer-url'}>
                    <h3 style={{ fontWeight: 400 }}>
                      Please set your {readableAuthName} consumer URL to the
                      following address:
                    </h3>
                    {`${window.location.protocol}//${window.location.host}/api`}
                    <wbr />
                    {`/portal/sessions/saml/${this.state.authenticationAdmin.organisation.slug}`}
                  </div>
                  <SAMLInput
                    issuer={
                      this.state.authenticationAdmin.samlProvider?.issuer || ''
                    }
                    ssoURL={
                      this.state.authenticationAdmin.samlProvider?.ssoURL || ''
                    }
                    certificate={
                      this.state.authenticationAdmin.samlProvider
                        ?.certificate || ''
                    }
                    onChangeSAMLTextfields={this.onChangeSAMLTextfields}
                  />
                </>
              )}
              {isVaultAuth && (
                <Vault2faInput
                  toggle2fa={this.toggle2FA}
                  authType={
                    this.state.authenticationAdmin.organisation
                      .authenticationType
                  }
                  toggleInfoElement={this.toggleInfoElement}
                  showInfoElement={this.state.showInfoElement}
                />
              )}
            </div>
            <div className="authentication-admin-button-container">
              <Button
                variant="primary"
                size="medium"
                width="120px"
                data-testid={'save-authentication'}
                loading={this.state.loading}
                onClick={
                  this.state.changedAuthenticationType
                    ? () => {
                        this.setState({
                          confirmSettingsDialogOpen: true,
                        });
                      }
                    : this.saveAuthenticationAdmin
                }
                disabled={!this.state.validAuthSettings}
              >
                <>
                  <StyledIconInButton
                    size="medium"
                    position="left"
                    className="uil uil-save"
                  />
                  Save
                </>
              </Button>
            </div>
          </form>
        </div>

        <OldConfirmDialog
          open={this.state.confirmSettingsDialogOpen}
          text={
            'Are you sure you want to change the authentication method for all Resolution Hub users?'
          }
          buttonText={'Save'}
          onSend={this.saveAuthenticationAdmin}
          onCancel={this.closeConfimSettingsDialog}
        />

        <OldConfirmDialog
          open={this.state.successDialogOpen}
          maxWidth={'sm'}
          buttonText="Dismiss"
          onSend={this.closeSuccessDialog}
        >
          <div className="auth-change-success-dialog">
            <span style={{ fontSize: '80px' }}>
              <AuthSuccessIcon />
            </span>

            <h1 className="authentication-admin-header">{`${readableAuthName} enabled successfully.`}</h1>
            <h1
              className="authentication-admin-header"
              style={{ fontWeight: 600 }}
            >
              Resolution Hub users can now only login using {readableAuthName}
            </h1>
            {(isOIDCAuth || isSAMLAuth) && (
              <div>
                <div className="dialog-spacing">{`To test everything is working correctly, Log Out of Resolution Hub by clicking the avatar in the top right and clicking Log Out.`}</div>
                <div className="dialog-spacing">{`Once logged out, enter your email address on the Resolution Hub log in page, you should be redirected to ${readableAuthName} to complete your log in.`}</div>
              </div>
            )}
            <div className="dialog-spacing">
              If you have any questions or issues please contact:{' '}
              <a className="link" href="mailto:techsupport@vaultplatform.com">
                techsupport@vaultplatform.com
              </a>
            </div>
          </div>
        </OldConfirmDialog>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: Types.State | any) => ({
  authenticationAdmin:
    state && state.organisation ? state.organisation.authenticationAdmin : null,
  authenticationType:
    state && state.organisation && state.organisation.organisation
      ? state.organisation.organisation.authenticationType
      : null,
  slug:
    state && state.organisation && state.organisation.organisation
      ? state.organisation.organisation.slug
      : null,
  features: state?.features ?? [],
});

export default connect(mapStateToProps, { updateAuthenticationAdmin })(
  AuthenticationAdmin,
);
